/**
 * Mount-time activity tracking for an inline sub-tab — §2.8 #142.
 *
 * `TabbedInterface` mounts only the active tab (React key-based
 * reconciliation), so a tab swap unmounts the previous wrapper and mounts the
 * new one — which is exactly the lifecycle `useActivityTracking` keys off. That
 * makes "how long was this sub-tab open" a mount duration rather than something
 * the tab component has to measure itself.
 *
 * It has to be a COMPONENT rather than a hook call inside the tab's content,
 * because the content is built as inline JSX in the parent's render. A hook
 * there would run on the parent's lifecycle and report the panel being open,
 * not the sub-tab.
 *
 * ---
 * Was duplicated verbatim in `ReferenceViewer.tsx` and `PracticeViewer.tsx`.
 * The two copies had already drifted in their comments but not their code;
 * deduped here before that became a behaviour difference.
 *
 * Note the two callers pass DIFFERENT content types, and deliberately:
 * Practice distinguishes `practice_flashcard` from `practice_quiz` because they
 * are separately meaningful, while all four Appendix sub-tabs share
 * `reference` — they aggregate to one "Reference" part for Coverage, and
 * overlapping rows of the same type are interval-unioned downstream.
 */
import type { ActivityContentType } from '@/lib/api-client';
import { useActivityTracking } from '@/hooks/useActivityTracking';

export interface ActivityTrackedTabProps {
  contentType: ActivityContentType;
  lessonId?: string;
  children: React.ReactNode;
}

export function ActivityTrackedTab({
  contentType,
  lessonId,
  children,
}: ActivityTrackedTabProps) {
  useActivityTracking({ contentType, lessonId });
  return <>{children}</>;
}
